import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArrowLeft, RotateCcw, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, calcEta } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function ArchivioOspiti() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { selectedStructure, structures = [], user } = useAuth();
  const structMap = Object.fromEntries(structures.map((s) => [s.id, s]));
  const canConv = ["super_admin", "admin", "responsabile", "segreteria"].includes(user?.role);

  const { data = [], isLoading } = useQuery({
    queryKey: ["ospiti-archivio", selectedStructure],
    queryFn: async () => (await api.get("/ospiti/archivio", { params: { structure_id: selectedStructure } })).data,
  });

  const restore = async (o) => {
    if (!window.confirm(`Ripristinare ${o.nome} ${o.cognome}? Tornerà tra gli ospiti attivi.`)) return;
    try {
      await api.post(`/ospiti/${o.id}/restore`);
      toast.success("Ospite ripristinato");
      qc.invalidateQueries({ queryKey: ["ospiti-archivio"] });
    } catch { toast.error("Errore nel ripristino"); }
  };

  return (
    <div>
      <Button variant="ghost" onClick={() => nav("/ospiti")} className="mb-3" data-testid="archivio-back"><ArrowLeft size={16} className="mr-1" /> Torna agli ospiti</Button>
      <div className="mb-5">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Archive size={22} className="text-primary" /> Archivio Ospiti</h1>
        <p className="text-sm text-muted-foreground">Ospiti dimessi o eliminati. Puoi ripristinarli in qualsiasi momento.</p>
      </div>

      <div className="gr-card overflow-x-auto" data-testid="archivio-ospiti">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary/60">
              <th className="text-left p-3 font-bold min-w-[200px]">Ospite</th>
              <th className="text-left p-3 font-bold">Struttura</th>
              <th className="text-left p-3 font-bold">Camera/zona</th>
              <th className="text-left p-3 font-bold">Ingresso</th>
              <th className="text-left p-3 font-bold">Dimissione</th>
              <th className="text-left p-3 font-bold">Motivo</th>
              <th className="p-3 font-bold text-center">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Caricamento...</td></tr>}
            {!isLoading && data.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Nessun ospite in archivio</td></tr>}
            {data.map((o) => (
              <tr key={o.id} className="border-t" data-testid={`archivio-row-${o.id}`}>
                <td className="p-3 font-semibold">{o.cognome} {o.nome}<span className="block text-xs font-normal text-muted-foreground">{calcEta(o.data_nascita)} anni</span></td>
                <td className="p-3">{structMap[o.structure_id]?.nome || "—"}</td>
                <td className="p-3">{o.camera || "—"}</td>
                <td className="p-3">{fmtDate(o.data_ingresso)}</td>
                <td className="p-3">{o.data_dimissione ? fmtDate(o.data_dimissione) : "—"}</td>
                <td className="p-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${o.motivo === "eliminato" ? "bg-red-100 text-red-700" : o.deceduto ? "bg-slate-200 text-slate-800" : "bg-amber-100 text-amber-800"}`} data-testid={`archivio-motivo-${o.id}`}>
                    {o.motivo === "eliminato" ? "Eliminato" : o.deceduto ? "Dimesso / deceduto/a" : "Dimesso"}
                  </span>
                </td>
                <td className="p-3 text-center whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => nav(`/ospiti/${o.id}`)} data-testid={`archivio-scheda-${o.id}`}><FolderOpen size={15} className="text-primary" /></Button>
                  {canConv && <Button size="sm" variant="outline" onClick={() => restore(o)} data-testid={`archivio-restore-${o.id}`} className="text-emerald-700 border-emerald-300"><RotateCcw size={14} className="mr-1" /> Ripristina</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
